// src/index.ts
import {
  analyticsConfigSchema,
  analyticsErrorCategorySchema,
  analyticsEventSchema,
  analyticsInteractionNameSchema,
  analyticsTenantKeySchema
} from "@hereby/core/analytics";
var clients = /* @__PURE__ */ new WeakMap();
var noopWorkflow = {
  dispose() {
    return void 0;
  },
  validationFailed() {
    return void 0;
  },
  submit() {
    return void 0;
  },
  complete() {
    return void 0;
  },
  fail() {
    return void 0;
  },
  unconfirmed() {
    return void 0;
  },
  retry() {
    return noopWorkflow;
  }
};
function safely(action) {
  try {
    return Promise.resolve(action()).catch(() => void 0);
  } catch {
    return Promise.resolve();
  }
}
function createAnalytics(input, transport, runtime = {}) {
  const parsed = analyticsConfigSchema.safeParse(input);
  const config = parsed.success ? parsed.data : void 0;
  const signature = JSON.stringify(config);
  const existing = clients.get(transport);
  if (existing) {
    if (existing.signature === signature) {
      return existing.client;
    }
    return {
      view() {
        return void 0;
      },
      event() {
        return void 0;
      },
      startWorkflow() {
        return noopWorkflow;
      },
      async setConsent() {
        return void 0;
      },
      resetContext() {
        return void 0;
      },
      setForeground() {
        return void 0;
      },
      async stop() {
        return void 0;
      }
    };
  }
  let consent = "denied";
  let stopped = false;
  let stopPromise;
  let generation = 0;
  let workflowGeneration = 0;
  let consentGeneration = 0;
  let consentQueue = Promise.resolve();
  let consentReady = false;
  let currentScreen;
  let currentReferrer = "";
  let lastView;
  let tenantKey;
  let foreground = true;
  let foregroundGeneration = 0;
  const active = /* @__PURE__ */ new Map();
  const now = () => {
    try {
      return runtime.now ? runtime.now() : globalThis.performance.now();
    } catch {
      return Number.NaN;
    }
  };
  const validEnvironment = () => {
    if (!config?.enabled || stopped) {
      return false;
    }
    try {
      const env = transport.environment();
      if (env.surface !== config.surface) {
        return false;
      }
      if (config.surface !== "web") {
        return config.nativeEnvironmentVerified && env.verified === true;
      }
      const hosts = config.destinationMode === "production" ? config.productionHosts : config.testHosts;
      return hosts.includes(env.host) && (env.protocol === "https:" || config.destinationMode === "test" && env.protocol === "http:");
    } catch {
      return false;
    }
  };
  const allowed = () => consent === "granted" && validEnvironment();
  const sanitizeReferrer = (raw) => {
    if (!config) {
      return "";
    }
    try {
      const url = new URL(raw);
      return url.protocol === "https:" && config.productionHosts.includes(url.hostname) && !url.username && !url.password && !url.port && config.screens.some((s) => s.path === url.pathname) ? `https://${url.hostname}${url.pathname}` : "";
    } catch {
      return "";
    }
  };
  const page = (id = currentScreen) => {
    const screen = config?.screens.find((s) => s.id === id);
    if (!(screen && config)) {
      return void 0;
    }
    const runtimeHost = transport.environment().host;
    const pageHost = config.surface === "web" && config.destinationMode === "production" && config.productionHosts.includes(runtimeHost) ? runtimeHost : config.canonicalHost;
    return {
      page_location: `https://${pageHost}${screen.path}`,
      page_title: screen.title,
      page_referrer: currentReferrer
    };
  };
  const transportConfig = () => {
    const initialPage = page();
    if (!config) {
      return void 0;
    }
    const destination = config.destinationMode === "production" ? config.productionDestination : config.testDestination;
    return destination ? { config, destination, initialPage } : void 0;
  };
  const emit = (name, extra = {}, screenId = currentScreen) => {
    if (!(allowed() && config && screenId)) {
      return;
    }
    const currentPage = page(screenId);
    if (!currentPage) {
      return;
    }
    const event = analyticsEventSchema.safeParse({
      event_name: name,
      params: {
        app_module: config.appModule,
        surface: config.surface,
        ui_screen: screenId,
        schema_version: 1,
        release_version: config.releaseVersion,
        ...currentPage,
        tenant_key: tenantKey,
        ...extra
      }
    });
    if (!event.success) {
      return;
    }
    const epoch = generation;
    const guard = () => epoch === generation && allowed() && consentReady;
    const initial = transportConfig();
    safely(async () => {
      await consentQueue;
      if (!(guard() && initial)) {
        return;
      }
      await transport.initialize?.(initial, guard);
      if (guard()) {
        await transport.send(event.data, guard);
      }
    });
  };
  const observe = () => {
    if (!(allowed() && currentScreen) || currentScreen === lastView) {
      return;
    }
    lastView = currentScreen;
    emit(config?.surface === "web" ? "page_view" : "screen_view");
  };
  const invalidate = () => {
    generation++;
    lastView = void 0;
    active.clear();
  };
  const startWorkflow = (code) => {
    if (!(allowed() && config?.workflows.includes(code) && currentScreen)) {
      return noopWorkflow;
    }
    const key = `${currentScreen}:${code}`;
    const existing2 = active.get(key);
    if (existing2) {
      return existing2;
    }
    const epoch = generation;
    const screenId = currentScreen;
    const screenEpoch = workflowGeneration;
    let disposed = false;
    let stage = "started";
    let submittedAt = Number.NaN;
    let submittedForeground = foregroundGeneration;
    let next;
    const errors = /* @__PURE__ */ new Set();
    const valid = () => !disposed && epoch === generation && screenEpoch === workflowGeneration && allowed();
    const finish = (result, error) => {
      if (!valid() || stage !== "submitted") {
        return;
      }
      const checkedError = error === void 0 ? void 0 : analyticsErrorCategorySchema.safeParse(error);
      if (checkedError && !checkedError.success) {
        return;
      }
      stage = "finished";
      if (active.get(key) === handle) {
        active.delete(key);
      }
      const duration = now() - submittedAt;
      emit(
        `workflow_${result}`,
        {
          workflow: code,
          result,
          error_category: checkedError?.success ? checkedError.data : void 0,
          duration_ms: foreground && submittedForeground === foregroundGeneration && Number.isFinite(duration) && duration >= 0 && duration <= 864e5 ? Math.floor(duration) : void 0
        },
        screenId
      );
    };
    const handle = {
      dispose() {
        disposed = true;
        if (active.get(key) === handle) {
          active.delete(key);
        }
      },
      validationFailed(error) {
        const checked = analyticsErrorCategorySchema.safeParse(error);
        if (valid() && stage === "started" && checked.success && !errors.has(error)) {
          errors.add(error);
          emit(
            "workflow_validation_failed",
            { workflow: code, result: "validation_failed", error_category: checked.data },
            screenId
          );
        }
      },
      submit() {
        if (!valid() || stage !== "started") {
          return;
        }
        stage = "submitted";
        submittedAt = foreground ? now() : Number.NaN;
        submittedForeground = foregroundGeneration;
        emit("workflow_submitted", { workflow: code, result: "submitted" }, screenId);
      },
      complete() {
        finish("completed");
      },
      fail(error) {
        finish("failed", error);
      },
      unconfirmed() {
        finish("unconfirmed");
      },
      retry() {
        if (!valid() || stage !== "finished") {
          return handle;
        }
        next ??= startWorkflow(code);
        return next;
      }
    };
    active.set(key, handle);
    emit("workflow_started", { workflow: code, result: "started" }, screenId);
    return handle;
  };
  const client = {
    view(screenId, referrer = "") {
      if (currentScreen !== screenId) {
        workflowGeneration++;
        active.clear();
      }
      if (!config?.screens.some((s) => s.id === screenId)) {
        currentScreen = void 0;
        lastView = void 0;
        return;
      }
      currentScreen = screenId;
      currentReferrer = sanitizeReferrer(referrer);
      observe();
    },
    event(name, code) {
      const validName = analyticsInteractionNameSchema.safeParse(name);
      if (validName.success && config?.eventCodes[validName.data]?.includes(code)) {
        emit(validName.data, { interaction_code: code });
      }
    },
    startWorkflow,
    setConsent(state) {
      const next = state === "granted" && validEnvironment() ? "granted" : "denied";
      if (next === consent && (next === "denied" || consentReady)) {
        return consentQueue;
      }
      consent = next;
      consentReady = false;
      const epoch = ++consentGeneration;
      invalidate();
      if (next === "denied") {
        tenantKey = void 0;
        safely(() => transport.revoke?.());
      }
      consentQueue = consentQueue.then(async () => {
        if (epoch !== consentGeneration) {
          return;
        }
        let succeeded = false;
        await safely(async () => {
          await transport.setConsent(next, transportConfig());
          succeeded = true;
        });
        if (epoch === consentGeneration && succeeded) {
          consentReady = next === "granted";
          if (consentReady) {
            observe();
          }
        }
      });
      return consentQueue;
    },
    resetContext(context) {
      invalidate();
      tenantKey = void 0;
      const checked = analyticsTenantKeySchema.safeParse(context?.tenantKey);
      if (config?.tenantPolicy && checked.success) {
        tenantKey = checked.data;
      }
      safely(() => transport.resetContext?.());
    },
    setForeground(value) {
      if (foreground !== value) {
        foreground = value;
        foregroundGeneration++;
      }
    },
    stop() {
      if (stopPromise) {
        return stopPromise;
      }
      stopped = true;
      consent = "denied";
      consentReady = false;
      consentGeneration++;
      invalidate();
      safely(() => transport.revoke?.());
      stopPromise = consentQueue.catch(() => void 0).then(async () => {
        if (transport.stop) {
          await transport.stop();
        } else {
          await safely(() => transport.setConsent("denied", transportConfig()));
        }
        if (clients.get(transport)?.client === client) {
          clients.delete(transport);
        }
      }).catch((error) => {
        stopPromise = void 0;
        throw error;
      });
      return stopPromise;
    }
  };
  safely(() => transport.revoke?.());
  consentQueue = safely(() => transport.setConsent("denied", transportConfig()));
  clients.set(transport, { signature, client });
  return client;
}
export {
  createAnalytics
};
//# sourceMappingURL=index.js.map